/* tslint:disable:import-spacing no-inferrable-types variable-name align no-trailing-whitespace */

import {Movement}        from '@/models/scenario/movement';
import {MovementDelay}   from '@/models/scenario/movement.delay';
import {Train}           from '@/models/scenario/train';
import {TrainClass}      from '@/models/scenario/train.class';
import {Logger}          from '@/util/logger';

import * as $            from 'jquery';
import 'bootstrap';

import {
  createGrid,
  GetGroupRowAggParams,
  GridOptions,
  GridReadyEvent,
  IRowNode
} from "ag-grid-community";

/**
 *
 * @class DelayReportDialog
 *
 * @brief provides a modal dialog for link/decision point delays summary report
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      November 2019
 *
 */
export class DelayReportDialog {

  /** @brief helper to format seconds as H:M:S string */
  public static formatSeconds(seconds: number): string {

    const timeofday: string = new Date(seconds * 1000).toISOString().substring(11, 11+8);
    const       day: string = Math.floor(seconds / 86400).toString().padStart(2, '0');
    let   formatted: string = 'DAY ' + day + ' ' + timeofday;

    if ((day === '0') || (day === '00')) {

      formatted = timeofday;
    }

    return formatted;
  }

  /** @brief pop the dialog for this train movement */
  public static show(move: Movement, uuid: number) {

    let content: string;

    Logger.debug('[delay-report]', 'filling content: ', move);

    const dly = DelayReportDialog.formatSeconds(move._totalDelay * 3600.0);
    const arr = DelayReportDialog.formatSeconds(move._arrival);
    const dpt = DelayReportDialog.formatSeconds(move._departure);

    content = `
<div id="delay-report-summary-group_${uuid}" class="delay-report-summary-group">
  <span>Dly: <strong class="timedata">${dly}</strong></span>
  <span>Arr: <strong class="timedata">${arr}</strong></span>
  <span>Dep: <strong class="timedata">${dpt}</strong></span>
</div>
<div
  id="ag-grid-delay-report-table_${uuid}"
  style="width: 100%; height: 400px;"
  class="ag-theme-balham-dark">
</div>`;

    let   title: string = '';
    const train: Train      = move._parent._train;

    title += train.generatedName();
    title += ' ' + TrainClass.typeToString(train._tc._dir);
    title += ' #' + train._id;
    title += ' Gen: ' + train.generatedID();

    const  html: string = `
<div id="delay-report-dialog_${uuid}" class="modal" style="padding: 0;" tabindex="-1" role="dialog">
  <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
    <div class="modal-content" style="padding:8px; background-color: #3C3F41;">
      <div class="modal-header">
        <h5 class="modal-title">Delay Report - ${title}</h5>
        <button type="button" class="close" data-dismiss="modal" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div class="modal-body">
        ${content}
      </div>
      <div class="modal-footer">
        <button type="button" class="ok-button btn btn-primary">Ok</button>
      </div>
    </div>
  </div>
</div>`;

    /* create and show the dialog */

    Logger.debug('[delay-report]', 'popping..');

    $(`#three-string-viewer-container_${uuid}`).append(html);
    // @ts-ignore
    $(`#delay-report-dialog_${uuid}`).modal({});

    /* register a cleanup when it's done */

    $(`#delay-report-dialog_${uuid} button.ok-button`).on('click', (e) => {

      // @ts-ignore
      $(`#delay-report-dialog_${uuid}`).modal('hide');

    });

    $(`#delay-report-dialog_${uuid}`).on('hidden.bs.modal', (e) => {

      Logger.debug('[delay-report]', 'destructing..');

      $(`#delay-report-dialog_${uuid}`).remove();

    });

    /* dynamically create an ag-grid table ... */

    const id: string = `#ag-grid-delay-report-table_${uuid}`;

    Logger.debug(`[delay-report] waiting for host DOM... ${id}`);

    function waitForRender(time: DOMHighResTimeStamp): void {

      /*
       * instead of trying to do a timeout on the existence of the DOM, we check
       * if it exists AND has a rendered geometry...if not we just request an
       * animation frame. It will typically only take a couple of frames before
       * it gets rendered; Javascript is single threaded, so Angular or whatever
       * will do all of its jazz, and this element will pop up in the next frame.
       *
       */

      if ($(id).length == 0) {

        /* the window container has not even been created */

        window.requestAnimationFrame(waitForRender);

        /* keep waiting */

        return;
      }

      /* hosting element is ready...do our setup... */

      let el: HTMLElement = $(id).get()[0];

      Logger.debug('[delay-report] building on host DOM: ', el);

      /* setup table for automatic aggregation (roll up) ... */

      const columnDefs = [
        {
          headerName: 'Category',
          field: 'category',
          width: 120,
          rowGroup: true,
          hide: true
        },
        {
          headerName: 'Subcategory',
          field: 'subcategory',
          width: 120,
          rowGroup: true,
          hide: true
        },
        {
          headerName: 'Hours',
          field: 'hours',
          width: 100,
          valueFormatter: (params) => {

            return DelayReportDialog.formatSeconds(params.value * 3600.0);
          }
        }
      ];

      /* create the actual data */

      const rowData: any[] = [];

      for (const item of move._delays) {

        const c: string = MovementDelay.typeString(item._type);
        const s: string = MovementDelay.subTypeString(item._type);
        const h: number = item._hours;

        const row = {
          category:    c,
          subcategory: s,
          hours:       h
        };

        rowData.push(row);
      }

      /* configure ... */

      const gridOptions: GridOptions = {

        columnDefs,

        rowData,

        onGridReady: (e: GridReadyEvent): void => {

          Logger.debug('[delay-report]', 'ready!');

        },

        enableRangeSelection: true,
        sideBar: true,

        defaultColDef:        {
          sortable: true,
          filter: true
        },

        autoGroupColumnDef:   {
          headerName: 'Delay',
          field: 'delay',
          width: 200
        },

        getGroupRowAgg : (params: GetGroupRowAggParams) => {

          const  result = {
            hours: 0.0
          };

          params.nodes.forEach((node: IRowNode): void => {

            const  data = node.group ? node.aggData : node.data;

            if (typeof data.hours === 'number') {

              result.hours += data.hours;
            }

          });

          return result;
        }
      };

      /* render */

      createGrid(el, gridOptions);

    }

    window.requestAnimationFrame(waitForRender);
  }
}

